/**
 * SaveAsInvestigationMenu — the Merlin chat's "💾 Save as investigation"
 * header button. It opens the "Where should this go?" dialog
 * (SaveDestinationDialog, proposal 8): a new case with an editable title,
 * or one of the open cases for the same customer, with Merlin's
 * recommendation stated. Nothing is attached without that explicit choice
 * (a dented-drum complaint once landed silently in the paint-coverage case).
 * Focus returns to this button on Cancel / Escape; on a save the page shows
 * the confirmation (SavedCaseNotice).
 */
import { useRef, useState } from 'react';

import { failedActionMessage } from './investigationsView';
import {
  attachInvestigationConversation,
  createInvestigation,
  type DoorInvestigationRead,
  type DoorThreadRead,
} from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { trackEvent } from '../../services/portalTracking';
import type { CopilotTurn } from '../../pages/qualityCopilotTurns';
import {
  conversationTextFromTurns,
  saveDestination,
  type SaveDestination,
} from './saveDestinationView';
import { SaveDestinationDialog, type SaveChoice } from './SaveDestinationDialog';

export type SaveOutcome = 'new' | 'attach';

export interface SaveAsInvestigationMenuProps {
  /** The ACTIVE conversation being saved/attached. */
  conversationId: string;
  /** The conversation's title (Merlin names each chat), or null. */
  conversationTitle: string | null;
  /** The conversation so far: its first message is the subject line, and
   *  what it names picks the customer. */
  turns: CopilotTurn[];
  /** The current case list; the dialog offers the same customer's open ones. */
  investigations: DoorInvestigationRead[];
  /** Merlin's threads: a chat that is a thread's conversation brings its
   *  customer and proposed case file. */
  threads: DoorThreadRead[];
  /** The door's asks, for each case's "waiting on …". */
  asks: DoorDataRequestRead[];
  /** Style the trigger like the page's other header pills. */
  pillClassName: string;
  onSaved: (investigation: DoorInvestigationRead, outcome: SaveOutcome) => void;
}

export function SaveAsInvestigationMenu({
  conversationId,
  conversationTitle,
  turns,
  investigations,
  threads,
  asks,
  pillClassName,
  onSaved,
}: SaveAsInvestigationMenuProps) {
  // The choices are computed once per opening and held (not derived on each
  // render): a 30s poll must not reshuffle them under the person's pointer.
  const [destination, setDestination] = useState<SaveDestination | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const recommendedCase =
    destination?.recommendation.kind === 'open_case'
      ? destination.recommendation.investigation
      : null;

  const openDialog = () => {
    const d = saveDestination({
      conversationId,
      conversationTitle,
      text: conversationTextFromTurns(turns),
      investigations,
      threads,
    });
    setError(null);
    setDestination(d);
    trackEvent('action', 'save_dialog_open', {
      conversation_id: conversationId,
      customer_no: d.customerNo,
      recommendation: d.recommendation.kind,
      recommended_investigation_no:
        d.recommendation.kind === 'open_case' ? d.recommendation.investigation.investigation_no : null,
      candidate_investigation_nos: d.candidates.map((c) => c.investigation_no),
    });
  };

  const close = () => {
    setDestination(null);
    setError(null);
    buttonRef.current?.focus();
  };

  const confirm = async (choice: SaveChoice) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const saved =
        choice.kind === 'new'
          ? await createInvestigation({ title: choice.title, conversation_id: conversationId })
          : await attachInvestigationConversation(choice.investigation.id, conversationId);
      trackEvent('action', 'save_dialog_confirm', {
        conversation_id: conversationId,
        choice: choice.kind,
        investigation_id: saved.id,
        investigation_no: saved.investigation_no,
        title_edited: choice.kind === 'new' ? choice.title !== destination?.proposedTitle : null,
        followed_recommendation:
          choice.kind === 'attach'
            ? recommendedCase?.id === choice.investigation.id
            : destination?.recommendation.kind !== 'open_case',
      });
      setDestination(null);
      onSaved(saved, choice.kind);
    } catch (e) {
      setError(
        choice.kind === 'new'
          ? failedActionMessage(`create the case “${choice.title}”`, e)
          : failedActionMessage(`add this conversation to ${choice.investigation.investigation_no}`, e),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={pillClassName}
        onClick={openDialog}
        title="Track this conversation as a quality investigation"
        aria-haspopup="dialog"
        aria-expanded={destination !== null}
      >
        💾 Save as investigation
      </button>
      {destination && (
        <SaveDestinationDialog
          destination={destination}
          asks={asks}
          busy={busy}
          error={error}
          onCancel={() => {
            trackEvent('action', 'save_dialog_cancel', { conversation_id: conversationId });
            close();
          }}
          onChoose={(kind, inv) =>
            trackEvent('action', 'save_dialog_choice', {
              conversation_id: conversationId,
              choice: kind,
              investigation_id: inv?.id ?? null,
              investigation_no: inv?.investigation_no ?? null,
            })
          }
          onConfirm={(choice) => void confirm(choice)}
        />
      )}
    </>
  );
}

export default SaveAsInvestigationMenu;
